import { z } from 'zod'
import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { homerunnerApiBaseUrl } from '../hr'
import { okOrThrow } from './http'
import { widgetFilterSchema } from './schema'
import { useShadowHost } from './shadow-host-context'
import { recordPagePropertyView } from './guest/page-property'

/**
 * Shared single-property "details" query.
 *
 * property-title, property-meta and property-map each derive their view from the
 * SAME `/public-api/v1/property-details` response. Keying them identically
 * (`['property-details', filter]`) means React Query fetches it ONCE per page —
 * a property-details PDP renders all three, and previously each hit the identical
 * URL under its own key (three duplicate round-trips). Each consumer projects the
 * shared raw result with its own `select`.
 *
 * NOTE: this canNOT be pre-seeded from the renderer's `fetchFullProperty`
 * (`/property/{slug}/full`) — that payload leaves `type`/`city` null and omits
 * `rating`, which `/property-details` resolves. So one real fetch of this
 * endpoint per PDP remains (down from three); the resolved fields aren't
 * available anywhere cheaper.
 */

export const propertyDetailsFilterSchema = widgetFilterSchema.extend({
  feed_id: z.number().int().describe('Filter by feed ID'),
  platform: z.string().nullish().describe('Filter by platform UUID'),
  property: z.string().describe('Filter by property slug'),
}).describe('Data filter options for property details')

export type PropertyDetailsFilter = z.infer<typeof propertyDetailsFilterSchema>

/**
 * The resolved single-property object returned by `/property-details`. Superset
 * of every PDP widget's needs; only the fields the widgets read are typed —
 * extra fields ride through untouched (they exist at runtime, TS just ignores
 * them, which is exactly how the per-widget fetchers treated this response
 * before this refactor).
 */
export type PropertyDetailsRaw = {
  id: number
  uuid: string
  name: string
  title: string
  slug?: string
  type: { id: number; name: string } | null
  city: { id: number; name: string } | null
  rating?: { average: number; count: number } | null
  latitude: string | number | null
  longitude: string | number | null
  base_daily_rate: number
  currency_code?: string
  bedroom_count: number
  bathroom_count: string | number
  bed_count?: number
  base_guest_count: number
  maximum_guest_count: number
  area_size?: number | null
  area_size_unit?: string | null
  platform_id?: number
  source?: string
  /**
   * Term lists as `{id,name}[]`, the explorer-list shape (central#3805 / #3784).
   * ABSENT on a Central that predates it: consumers must treat undefined as
   * "no signal", not as "no terms".
   */
  tags?: { id: number; name: string }[]
  groups?: { id: number; name: string }[]
}

export function propertyDetailsKey(filter: PropertyDetailsFilter) {
  return ['property-details', filter] as const
}

export async function fetchPropertyDetails(filter: PropertyDetailsFilter): Promise<PropertyDetailsRaw> {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-details`)
  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
  })
  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch property details')
  const data = await res.json() as { status: 'ok' | 'error'; result: PropertyDetailsRaw }
  return data.result
}

/**
 * Consume the shared property-details query. `select` projects the raw result to
 * the widget's own view (runs per-observer, so different widgets share one cache
 * entry + one fetch while each gets its own shape). `enabled` defaults to true to
 * preserve each widget's prior fetch behavior.
 */
export function usePropertyDetails<T = PropertyDetailsRaw>(
  filter: PropertyDetailsFilter,
  opts?: { select?: (raw: PropertyDetailsRaw) => T; enabled?: boolean },
) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: propertyDetailsKey(filter),
    queryFn: () => fetchPropertyDetails(filter),
    select: opts?.select,
    enabled: opts?.enabled ?? true,
  })

  // Recently-viewed producer (#1138): a successful property-details result IS
  // a confirmed view of that property, on SSR-hydrated, CSR and manual embeds
  // alike (the shared query client hydrates this query from SSR state). Read
  // the RAW cache entry — `select` may have projected `uuid` away. Once per
  // distinct property per hook instance; list-level idempotency comes from
  // `pushRecentlyViewed`, and `recordPropertyView` validates the identifier.
  //
  // Page identity (#1151): only when the PAGE is about this property. A
  // property-title/meta/map widget pinned to a featured property (a manual
  // `data-hr-options` filter.property, or the dashboard Property select) on a
  // non-property page renders the same details on every load and must not
  // record. The signal is the pushed `filter.property` (or the resolved slug)
  // against the page's own URL/canonical, or the widget's host sitting inside
  // a generated property page — see `guest/page-property.ts`.
  const shadowHost = useShadowHost()
  const lastRecordedUuidRef = useRef<string | null>(null)
  useEffect(() => {
    if (!query.isSuccess) return
    const raw = queryClient.getQueryData<PropertyDetailsRaw>(propertyDetailsKey(filter))
    const uuid = raw?.uuid
    if (!uuid || lastRecordedUuidRef.current === uuid) return
    lastRecordedUuidRef.current = uuid
    recordPagePropertyView(uuid, { slugs: [filter.property, raw.slug], host: shadowHost })
  }, [query.isSuccess, query.dataUpdatedAt, queryClient, filter, shadowHost])

  return query
}
